import React, {
    ReactElement,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { useDomainContext } from "@hooks/useDomainContext";
import ProxiedNumber from "@components/shared/proxiedNumber";
import { useRouter } from "next/router";
import { gettingQueriesAsString } from "@lib/shared/routerQueriesToString";
import style from "./style.module.scss";
import { convertToBreakpointVariables } from "@page-builder/utils";
// import CloseIcon from "@components/svg/closeIcon";
import { getHomepageHref, SKIPPED_OPERATION_ID_KEY } from "@lib/sharedUtils";

export default function Header({
    slogan,
    children,
    textColor,
    iconColor,
    backGroundColor,
    isLogoClickable = true,
    emptyIcon,
    boxShadow,
    border,
    phoneNumberColor,
    stickyStyle,
    hideSticky,
    logoClassName,
    useChildrenLogo,
    bannerStyleAsProp,
    className,
    useSecondaryLogo = false,
}: {
    slogan?: string;
    children?: ReactNode;
    textColor?: string;
    iconColor?: string;
    backGroundColor?: string;
    isLogoClickable?: boolean;
    emptyIcon?: JSX.Element;
    boxShadow?: string;
    border?: string;
    phoneNumberColor?: string;
    stickyStyle?: {
        stickTitleColor?: string;
        stickBtnTxtColor?: string;
        stickBtnBgColor?: string;
        stickBtnBorderColor?: string;
        stickyPulseColor?: string;
    };
    hideSticky?: boolean;
    logoClassName?: string;
    useChildrenLogo?: boolean;
    bannerStyleAsProp?: string;
    className?: string;
    useSecondaryLogo?: boolean;
}): ReactElement {
    const {
        domain,
        blogPost,
        routeConfig,
        showFormModal,
        locale,
        defaultLocale,
        pageType,
    } = useDomainContext();

    const [headerHeight, setHeaderHeight] = useState<string | null>(null);
    const header = useRef<HTMLHeadElement>(null);
    const [bannerActive, setBannerActive] = useState<boolean>(false);
    const [hidePhoneNumber, setHidePhoneNumber] = useState<boolean>(false);

    const { query, asPath } = useRouter();

    const bannerTextBlock = domain.textBlocks?.find(
        (textBlock) => textBlock.position === "banner",
    );

    function showingBanner() {
        if (!bannerTextBlock) return false;
        if (pageType === "page-builder") {
            return !routeConfig?.config?.settings?.preHeaderText;
        }

        return true;
    }

    useEffect(() => {
        let prevHeight = "0";
        const observer = new ResizeObserver(([headerEntry]) => {
            if (header.current) {
                const height =
                    headerEntry.borderBoxSize?.[0]?.blockSize.toFixed(0);
                if (height !== prevHeight) {
                    prevHeight = height;
                    setHeaderHeight(`${height}px`);
                }
            }
        });

        if (header.current) {
            observer.observe(header.current);
        }
        if (bannerTextBlock) {
            setTimeout(() => {
                setBannerActive(true);
            }, 500);
        }

        if (
            sessionStorage.getItem(SKIPPED_OPERATION_ID_KEY) &&
            asPath.includes("thank-you")
        ) {
            setHidePhoneNumber(true);
        }

        return () => {
            if (header.current) {
                observer.unobserve(header.current);
            }
        };
    }, []);

    const logoFromVariantConfig =
        routeConfig?.templateConfig?.variantConfig?.logo;

    const logoPath =
        logoFromVariantConfig ??
        routeConfig?.config?.settings?.logo ??
        domain?.logo?.fullPath;

    const secondaryLogoPath = domain?.secondaryLogo?.fullPath ?? null;

    const callIconUnWrappedImage =
        routeConfig?.templateConfig?.headerAndFooter?.header
            ?.callIconUnWrappedImage;

    const phoneNumberBlock = (
        <div className="slogan items-center flex">
            <ProxiedNumber
                slogan={slogan}
                textColor={textColor}
                iconColor={iconColor ?? textColor}
                emptyIcon={emptyIcon}
                phoneNumberColor={phoneNumberColor}
                stickyStyle={stickyStyle}
                hideSticky={hideSticky || showFormModal}
                iconPath={callIconUnWrappedImage}
            />
        </div>
    );

    const finalUrl = useMemo(() => {
        if (!isLogoClickable) {
            return undefined;
        }
        const homepageHref = getHomepageHref(query, locale, defaultLocale);
        if (homepageHref === undefined) {
            return undefined;
        }
        const withQueryParams = gettingQueriesAsString(query, "start", [
            "genericRoute",
            "keyword",
            "category",
            "formTemplate",
            "tool",
            "slug",
            "data",
            "zc",
            "filters",
            "pii-token",
        ]);
        return `${homepageHref}?${withQueryParams}`;
    }, [isLogoClickable, query]);

    const variables = routeConfig?.config?.header?.variables;

    const styles = {
        backgroundColor: backGroundColor,
        boxShadow: boxShadow,
        borderBottom: border,
        ...(variables ? convertToBreakpointVariables(variables) : {}),
    };

    return (
        <header
            style={styles}
            className={`${style.header} ${className as string}`}
            ref={header}
        >
            <div
                className={`ltrStyle inner py-5 lg:py-4 px-5 flex lg:max-w-7xl mx-auto  lg:px-6 items-center ${style["headerContainer"]} `}
            >
                <div
                    className={`${style.logo} ${
                        logoClassName ? logoClassName : ""
                    }`}
                    style={{
                        backgroundImage: `url(${
                            (useSecondaryLogo
                                ? secondaryLogoPath
                                : logoPath ?? "") as string
                        })`,
                        width: "auto",
                        height: "auto",
                        backgroundSize: "contain",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        ...(domain.name === "homewarranty.deals"
                            ? { maxWidth: "330px" }
                            : {}),
                    }}
                >
                    <a href={finalUrl} aria-label={`${domain.name} logo`}>
                        {logoPath && !useChildrenLogo ? (
                            <img
                                src={logoPath}
                                className="h-auto w-full"
                                alt={`${domain.name} logo`}
                                style={{ visibility: "hidden" }}
                                rel="preload"
                            />
                        ) : (
                            <span className="children:w-full children:h-auto lg:children:h-10">
                                {children}
                            </span>
                        )}
                    </a>
                </div>
                <div>
                    {blogPost && (
                        <div className="text-xs lg:flex hidden justify-end  text-[#6b6b6b] ">
                            Advertisement
                        </div>
                    )}
                    {!hidePhoneNumber && phoneNumberBlock}
                </div>
                {headerHeight && (
                    <style>{`:root {--header-height: ${headerHeight} }`}</style>
                )}
            </div>
            {showingBanner() && (
                <div
                    className={`${style.banner} ${bannerStyleAsProp ?? ""} ${
                        bannerActive ? style.active : ""
                    }`}
                    style={
                        {
                            "--banner-background-color":
                                domain?.config?.bannerBgColor,
                            "--banner-text-color":
                                domain?.config?.bannerTextColor,
                        } as React.CSSProperties
                    }
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: bannerTextBlock?.text ?? "",
                        }}
                        className={style["banner-inner"]}
                    ></div>
                    {/* <button
                        onClick={() => {
                            setBannerActive(false);
                        }}
                        type="button"
                        aria-label="Close"
                        className={style["close-button"]}
                    >
                        <span className="close-icon">
                            <CloseIcon />
                        </span>
                    </button> */}
                </div>
            )}
        </header>
    );
}
